// ── Cowork Panel ─────────────────────────────────────────────
// Claude-style artifact workspace: tree nodes become tabbed,
// previewable, editable, versionable artifacts.

import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { ChatMarkdown } from './ChatPanel';
import { getNodeConfig } from './nodeConfig';

// ── Artifact derivation ──────────────────────────────────────
// Filters tree nodes into "significant" artifacts for tabs.

const PROMOTED_TYPES = new Set([
  'seed', 'feature', 'problem', 'insight', 'recommendation',
  'strategy', 'milestone', 'option', 'user_segment', 'job_to_be_done',
  'metric', 'constraint', 'risk', 'hypothesis', 'experiment',
]);

const GOOGLE_ICONS = {
  'google-doc': '\u{1F4C4}',
  'google-slides': '\u{1F4D1}',
  'google-sheet': '\u{1F4CA}',
};
const GOOGLE_COLORS = {
  'google-doc': '#4285f4',
  'google-slides': '#fbbc04',
  'google-sheet': '#34a853',
};

function deriveArtifacts(nodes, canvasArtifacts, googleArtifacts) {
  const artifacts = [];
  const seen = new Set();

  // 1. Seed node → "Overview" (always first)
  const seed = nodes.find(n => n.data?.type === 'seed');
  if (seed) {
    artifacts.push({
      id: seed.id,
      title: 'Overview',
      type: 'seed',
      contentType: 'markdown',
      content: buildNodeContent(seed),
      sourceNodeId: seed.id,
      icon: '◈',
      color: '#a78bfa',
    });
    seen.add(seed.id);
  }

  // 2. Depth-1 nodes (primary branches)
  const depth1 = nodes.filter(n =>
    !seen.has(n.id) && n.data?.depth === 1
  ).sort((a, b) => {
    // Sort by type priority, then label
    const aP = PROMOTED_TYPES.has(a.data?.type) ? 0 : 1;
    const bP = PROMOTED_TYPES.has(b.data?.type) ? 0 : 1;
    return aP - bP || (a.data?.label || '').localeCompare(b.data?.label || '');
  });

  for (const node of depth1) {
    const cfg = getNodeConfig(node.data?.type);
    artifacts.push({
      id: node.id,
      title: node.data?.label || node.data?.type || 'Node',
      type: node.data?.type || 'insight',
      contentType: 'markdown',
      content: buildNodeContent(node),
      sourceNodeId: node.id,
      icon: cfg.icon || '?',
      color: cfg.color || '#666',
    });
    seen.add(node.id);
  }

  // 3. High-score nodes at deeper levels
  const highScore = nodes.filter(n =>
    !seen.has(n.id) && (n.data?.score ?? 0) >= 7 && PROMOTED_TYPES.has(n.data?.type)
  ).sort((a, b) => (b.data?.score ?? 0) - (a.data?.score ?? 0))
    .slice(0, 8); // cap to avoid tab overload

  for (const node of highScore) {
    const cfg = getNodeConfig(node.data?.type);
    artifacts.push({
      id: node.id,
      title: node.data?.label || node.data?.type || 'Node',
      type: node.data?.type || 'insight',
      contentType: 'markdown',
      content: buildNodeContent(node),
      sourceNodeId: node.id,
      icon: cfg.icon || '?',
      color: cfg.color || '#666',
    });
    seen.add(node.id);
  }

  // 4. Canvas artifacts (HTML)
  if (canvasArtifacts?.length) {
    for (const ca of canvasArtifacts) {
      artifacts.push({
        id: `canvas-${ca.id}`,
        title: ca.title || ca.type || 'Canvas',
        type: ca.type || 'canvas',
        contentType: 'html',
        content: ca.html || '',
        sourceNodeId: null,
        icon: ca.type === 'dashboard' ? '?' : ca.type === 'timeline' ? '?' : ca.type === 'mockup' ? '?' : '?',
        color: '#6c63ff',
      });
    }
  }

  // 5. Google Workspace artifacts (Doc, Slides, Sheet)
  if (googleArtifacts?.length) {
    for (const ga of googleArtifacts) {
      artifacts.push({
        id: ga.id,
        title: ga.title || ga.type || 'Google Doc',
        type: ga.type,
        contentType: ga.type, // 'google-doc' | 'google-slides' | 'google-sheet'
        content: '', // no raw content — rendered via iframe
        previewUrl: ga.previewUrl,
        editUrl: ga.url,
        sourceNodeId: null,
        icon: GOOGLE_ICONS[ga.type] || '\u{1F4C4}',
        color: GOOGLE_COLORS[ga.type] || '#4285f4',
      });
    }
  }

  return artifacts;
}

function buildNodeContent(node) {
  const d = node.data || {};
  let md = '';
  if (d.label) md += `## ${d.label}\n\n`;
  if (d.type && d.type !== 'seed') {
    const cfg = getNodeConfig(d.type);
    md += `**${cfg.label || d.type.toUpperCase()}**`;
    if (d.score != null) md += ` | Score: ${d.score}/10`;
    md += '\n\n';
  }
  if (d.reasoning) md += d.reasoning + '\n';
  return md;
}

// ── Components ───────────────────────────────────────────────

function CoworkTabBar({ artifacts, activeId, onSelect, isGenerating }) {
  const scrollRef = useRef(null);

  return (
    <div className="cowork-tab-bar" ref={scrollRef}>
      {artifacts.map(a => (
        <button
          key={a.id}
          className={`cowork-tab${a.id === activeId ? ' active' : ''}`}
          style={a.id === activeId ? { borderBottomColor: a.color, color: a.color } : {}}
          onClick={() => onSelect(a.id)}
          title={a.title}
        >
          <span className="cowork-tab-icon">{a.icon}</span>
          <span className="cowork-tab-label">{a.title}</span>
        </button>
      ))}
      {isGenerating && (
        <span className="cowork-tab cowork-tab--generating">
          <span className="cowork-generating-dot" /> generating...
        </span>
      )}
    </div>
  );
}

function ArtifactToolbar({
  viewToggle, onToggleView,
  onCopy, onDownload,
  versionCount, versionIdx, onVersionBack, onVersionForward,
  hasUnsaved, onSave,
  contentType, editUrl,
}) {
  const isGoogle = contentType?.startsWith('google-');
  return (
    <div className="cowork-toolbar">
      <div className="cowork-toolbar-left">
        {/* Preview / Code toggle */}
        <div className="cowork-view-toggle">
          <button
            className={`cowork-vt-btn${viewToggle === 'preview' ? ' active' : ''}`}
            onClick={() => onToggleView('preview')}
          >Preview</button>
          <button
            className={`cowork-vt-btn${viewToggle === 'code' ? ' active' : ''}`}
            onClick={() => onToggleView('code')}
          >{contentType === 'html' ? 'HTML' : 'Source'}</button>
        </div>

        {/* Version nav */}
        {versionCount > 1 && (
          <div className="cowork-version-nav">
            <button
              className="cowork-ver-btn"
              disabled={versionIdx <= 0}
              onClick={onVersionBack}
            >&lsaquo;</button>
            <span className="cowork-ver-label">v{versionIdx + 1} of {versionCount}</span>
            <button
              className="cowork-ver-btn"
              disabled={versionIdx >= versionCount - 1}
              onClick={onVersionForward}
            >&rsaquo;</button>
          </div>
        )}
      </div>

      <div className="cowork-toolbar-right">
        {hasUnsaved && (
          <button className="cowork-save-btn" onClick={onSave}>Save</button>
        )}
        {isGoogle && editUrl && (
          <button className="cowork-action-btn cowork-google-open-btn" onClick={() => window.open(editUrl, '_blank')} title="Open in Google">
            Open in Google
          </button>
        )}
        {!isGoogle && <button className="cowork-action-btn" onClick={onCopy} title="Copy content">Copy</button>}
        {!isGoogle && <button className="cowork-action-btn" onClick={onDownload} title="Download file">Download</button>}
      </div>
    </div>
  );
}

function ArtifactViewer({ artifact, viewToggle, editDraft, onEditChange, versionContent }) {
  const displayContent = versionContent ?? artifact?.content ?? '';

  if (!artifact) {
    return (
      <div className="cowork-viewer cowork-viewer--empty">
        <div className="cowork-empty-state">
          <span className="cowork-empty-icon">?</span>
          <p>Generate a tree to see artifacts here</p>
        </div>
      </div>
    );
  }

  // Code / Source mode
  if (viewToggle === 'code') {
    return (
      <div className="cowork-viewer cowork-viewer--code">
        <textarea
          className="cowork-editor"
          value={editDraft ?? displayContent}
          onChange={e => onEditChange(e.target.value)}
          spellCheck={false}
        />
      </div>
    );
  }

  // Preview mode — HTML in iframe
  if (artifact.contentType === 'html') {
    return (
      <div className="cowork-viewer cowork-viewer--preview">
        <iframe
          title={artifact.title}
          srcDoc={displayContent}
          className="cowork-iframe"
          sandbox="allow-scripts"
        />
      </div>
    );
  }

  // Preview mode — Google Workspace iframe
  if (artifact.contentType?.startsWith('google-')) {
    return (
      <div className="cowork-viewer cowork-viewer--preview cowork-viewer--google">
        <iframe
          title={artifact.title}
          src={artifact.previewUrl}
          className="cowork-iframe cowork-google-iframe"
          allow="autoplay"
        />
      </div>
    );
  }

  // Preview mode — Markdown
  return (
    <div className="cowork-viewer cowork-viewer--preview">
      <div className="cowork-markdown">
        <ChatMarkdown content={displayContent} />
      </div>
    </div>
  );
}

// ── Right-side artifact list (like Claude Desktop's Context panel) ──

function ArtifactListPanel({ artifacts, activeId, onSelect }) {
  const googleArts = artifacts.filter(a => a.contentType?.startsWith('google-'));
  const treeArts = artifacts.filter(a => !a.contentType?.startsWith('google-') && a.contentType !== 'html');
  const canvasArts = artifacts.filter(a => a.contentType === 'html');

  return (
    <div className="cowork-artifact-list">
      <div className="cowork-al-header">Artifacts</div>

      {googleArts.length > 0 && (
        <div className="cowork-al-group">
          <div className="cowork-al-group-label">Google Workspace</div>
          {googleArts.map(a => (
            <button
              key={a.id}
              className={`cowork-al-item${a.id === activeId ? ' active' : ''}`}
              onClick={() => onSelect(a.id)}
            >
              <span className="cowork-al-icon" style={{ color: a.color }}>{a.icon}</span>
              <span className="cowork-al-title">{a.title}</span>
              <span className={`cowork-al-badge cowork-al-badge--${a.type}`}>
                {a.type === 'google-doc' ? 'DOC' : a.type === 'google-slides' ? 'SLIDES' : 'SHEET'}
              </span>
            </button>
          ))}
        </div>
      )}

      {canvasArts.length > 0 && (
        <div className="cowork-al-group">
          <div className="cowork-al-group-label">Canvas</div>
          {canvasArts.map(a => (
            <button
              key={a.id}
              className={`cowork-al-item${a.id === activeId ? ' active' : ''}`}
              onClick={() => onSelect(a.id)}
            >
              <span className="cowork-al-icon">{a.icon}</span>
              <span className="cowork-al-title">{a.title}</span>
            </button>
          ))}
        </div>
      )}

      {treeArts.length > 0 && (
        <div className="cowork-al-group">
          <div className="cowork-al-group-label">Tree Artifacts ({treeArts.length})</div>
          {treeArts.map(a => (
            <button
              key={a.id}
              className={`cowork-al-item${a.id === activeId ? ' active' : ''}`}
              onClick={() => onSelect(a.id)}
            >
              <span className="cowork-al-icon" style={{ color: a.color }}>{a.icon}</span>
              <span className="cowork-al-title">{a.title}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Google Artifact Card (for rendering in chat) ──────────────

export function GoogleArtifactCard({ artifact, onClickArtifact }) {
  const typeLabel = artifact.type === 'google-doc' ? 'Document' : artifact.type === 'google-slides' ? 'Presentation' : 'Spreadsheet';
  const typeExt = artifact.type === 'google-doc' ? 'DOCS' : artifact.type === 'google-slides' ? 'SLIDES' : 'SHEETS';
  const color = GOOGLE_COLORS[artifact.type] || '#4285f4';
  const icon = GOOGLE_ICONS[artifact.type] || '\u{1F4C4}';

  return (
    <div className="cowork-artifact-card" onClick={() => onClickArtifact?.(artifact.id)}>
      <div className="cowork-artifact-card-icon" style={{ background: color + '18', color }}>
        {icon}
      </div>
      <div className="cowork-artifact-card-info">
        <div className="cowork-artifact-card-title">{artifact.title}</div>
        <div className="cowork-artifact-card-meta">{typeLabel} · {typeExt}</div>
      </div>
      <button
        className="cowork-artifact-card-drive-btn"
        onClick={(e) => { e.stopPropagation(); window.open(artifact.url || artifact.editUrl, '_blank'); }}
      >
        <span className="cowork-drive-icon">G</span> Google Drive
      </button>
    </div>
  );
}

// ── Main CoworkPanel ─────────────────────────────────────────

export default function CoworkPanel({
  nodes = [],
  canvasArtifacts = [],
  googleArtifacts = [],
  idea,
  mode,
  isGenerating,
  onNodeUpdate,
  onSelectArtifact,
}) {
  const [activeArtifactId, setActiveArtifactId] = useState(null);
  const [viewToggle, setViewToggle] = useState('preview');
  const [editDraft, setEditDraft] = useState(null);
  // versions: Map<artifactId, { snapshots: string[], currentIdx: number }>
  const [versions, setVersions] = useState({});
  const [copyFeedback, setCopyFeedback] = useState(false);

  // Derive artifacts from nodes
  const artifacts = useMemo(
    () => deriveArtifacts(nodes, canvasArtifacts, googleArtifacts),
    [nodes, canvasArtifacts, googleArtifacts]
  );

  // Auto-select first artifact if none selected or selection is gone
  useEffect(() => {
    if (artifacts.length === 0) { setActiveArtifactId(null); return; }
    if (!activeArtifactId || !artifacts.find(a => a.id === activeArtifactId)) {
      setActiveArtifactId(artifacts[0].id);
    }
  }, [artifacts, activeArtifactId]);

  // Initialize version for new artifacts
  useEffect(() => {
    for (const a of artifacts) {
      if (!versions[a.id]) {
        setVersions(prev => ({
          ...prev,
          [a.id]: { snapshots: [a.content], currentIdx: 0 },
        }));
      }
    }
  }, [artifacts]); // eslint-disable-line react-hooks/exhaustive-deps

  const activeArtifact = artifacts.find(a => a.id === activeArtifactId) || null;
  const activeVersions = versions[activeArtifactId] || { snapshots: [], currentIdx: 0 };
  const versionContent = activeVersions.snapshots[activeVersions.currentIdx] ?? null;

  // Reset edit draft when switching tabs or toggle
  useEffect(() => {
    setEditDraft(null);
    setViewToggle('preview');
  }, [activeArtifactId]);

  const hasUnsaved = editDraft != null && editDraft !== (versionContent ?? activeArtifact?.content ?? '');

  // ── Handlers ──

  const handleSave = useCallback(() => {
    if (!activeArtifact || editDraft == null) return;
    const newContent = editDraft;

    // Push new version
    setVersions(prev => {
      const existing = prev[activeArtifact.id] || { snapshots: [activeArtifact.content], currentIdx: 0 };
      const snapshots = [...existing.snapshots, newContent];
      return { ...prev, [activeArtifact.id]: { snapshots, currentIdx: snapshots.length - 1 } };
    });

    // Propagate to node tree if it's a node-based artifact
    if (activeArtifact.sourceNodeId && onNodeUpdate) {
      // Extract reasoning from markdown (strip the header we added)
      const reasoning = newContent
        .replace(/^## .+\n\n/, '')
        .replace(/^\*\*[^*]+\*\*.*\n\n/, '')
        .trim();
      onNodeUpdate(activeArtifact.sourceNodeId, { reasoning });
    }

    setEditDraft(null);
  }, [activeArtifact, editDraft, onNodeUpdate]);

  const handleCopy = useCallback(() => {
    const content = versionContent ?? activeArtifact?.content ?? '';
    navigator.clipboard?.writeText(content);
    setCopyFeedback(true);
    setTimeout(() => setCopyFeedback(false), 1500);
  }, [versionContent, activeArtifact]);

  const handleDownload = useCallback(() => {
    const content = versionContent ?? activeArtifact?.content ?? '';
    const ext = activeArtifact?.contentType === 'html' ? '.html' : '.md';
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = (activeArtifact?.title || 'artifact').replace(/[^a-zA-Z0-9_-]/g, '_') + ext;
    a.click();
    URL.revokeObjectURL(url);
  }, [versionContent, activeArtifact]);

  const handleVersionBack = useCallback(() => {
    setVersions(prev => {
      const v = prev[activeArtifactId];
      if (!v || v.currentIdx <= 0) return prev;
      return { ...prev, [activeArtifactId]: { ...v, currentIdx: v.currentIdx - 1 } };
    });
    setEditDraft(null);
  }, [activeArtifactId]);

  const handleVersionForward = useCallback(() => {
    setVersions(prev => {
      const v = prev[activeArtifactId];
      if (!v || v.currentIdx >= v.snapshots.length - 1) return prev;
      return { ...prev, [activeArtifactId]: { ...v, currentIdx: v.currentIdx + 1 } };
    });
    setEditDraft(null);
  }, [activeArtifactId]);

  // Expose selectArtifact for external callers (e.g. chat card click)
  const handleSelectArtifact = useCallback((id) => {
    setActiveArtifactId(id);
  }, []);

  // Notify parent of select handler
  useEffect(() => {
    if (onSelectArtifact) onSelectArtifact(handleSelectArtifact);
  }, [onSelectArtifact, handleSelectArtifact]);

  // ── Render ──

  return (
    <div className="cowork-panel">
      <div className="cowork-main">
        {artifacts.length === 0 ? (
          <div className="cowork-viewer cowork-viewer--empty">
            <div className="cowork-empty-state">
              <span className="cowork-empty-icon">?</span>
              <p>Generate a tree to populate your workspace</p>
              <p className="cowork-empty-hint">Artifacts will appear here as tabs</p>
            </div>
          </div>
        ) : (
          <>
            <CoworkTabBar
              artifacts={artifacts}
              activeId={activeArtifactId}
              onSelect={setActiveArtifactId}
              isGenerating={isGenerating}
            />
            <ArtifactToolbar
              viewToggle={viewToggle}
              onToggleView={setViewToggle}
              onCopy={handleCopy}
              onDownload={handleDownload}
              versionCount={activeVersions.snapshots.length}
              versionIdx={activeVersions.currentIdx}
              onVersionBack={handleVersionBack}
              onVersionForward={handleVersionForward}
              hasUnsaved={hasUnsaved}
              onSave={handleSave}
              contentType={activeArtifact?.contentType}
              editUrl={activeArtifact?.editUrl}
            />
            {copyFeedback && <div className="cowork-copy-toast">Copied!</div>}
            <ArtifactViewer
              artifact={activeArtifact}
              viewToggle={viewToggle}
              editDraft={editDraft}
              onEditChange={setEditDraft}
              versionContent={versionContent}
            />
          </>
        )}
      </div>
      {artifacts.length > 0 && (
        <ArtifactListPanel
          artifacts={artifacts}
          activeId={activeArtifactId}
          onSelect={setActiveArtifactId}
        />
      )}
    </div>
  );
}
